import { useEffect, useRef, useState } from 'react';
import { Camera, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

/**
 * Mobile-first photo picker. `capture` hints the camera on phones; users can
 * still choose from the library. Shows a live preview from the selected File.
 */
export function PhotoPicker({
  file,
  existingUrl,
  onChange,
}: {
  file: File | null;
  existingUrl?: string;
  onChange: (file: File | null) => void;
}) {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const [preview, setPreview] = useState<string | null>(null);

  useEffect(() => {
    if (!file) {
      setPreview(null);
      return;
    }
    const url = URL.createObjectURL(file);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const shown = preview ?? existingUrl ?? null;

  return (
    <div>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="sr-only"
        aria-label={t('item.photo')}
        onChange={(e) => onChange(e.target.files?.[0] ?? null)}
      />
      {shown ? (
        <div className="relative w-full aspect-[4/3] rounded-lg overflow-hidden surface">
          <img src={shown} alt="" className="w-full h-full object-cover" />
          <button
            type="button"
            onClick={() => {
              onChange(null);
              if (inputRef.current) inputRef.current.value = '';
            }}
            className="absolute top-2 end-2 bg-ink/70 text-white rounded-full p-1.5"
            aria-label={t('item.removePhoto')}
          >
            <X className="size-4" />
          </button>
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            className="absolute bottom-2 end-2 bg-surface/90 text-ink text-[13px] font-medium rounded-md px-3 py-1.5 shadow-card"
          >
            {t('item.changePhoto')}
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          className="w-full aspect-[4/3] rounded-lg border-2 border-dashed border-rule-strong bg-surface-tint flex flex-col items-center justify-center gap-2 text-graphite hover:border-petrol hover:text-petrol transition-colors"
        >
          <Camera className="size-8" />
          <span className="text-[15px] font-medium">{t('item.addPhoto')}</span>
        </button>
      )}
    </div>
  );
}
